import { useEffect } from 'react';

/**
 * Subscribe to the global `slimmy:push` event bus.
 *
 * `App.js` dispatches `slimmy:push` on the window every time a push
 * notification is delivered to the foreground (PWA) or tapped on a
 * native build. Any component that wants live, push-driven refresh
 * (instead of relying on a 30 s polling timer) can subscribe to it
 * with a single line:
 *
 *     usePushBus(() => refetch());
 *
 * The optional `filter` lets you react only to a subset — e.g. only
 * pushes whose `data.open === 'assistenza'` or whose `data.type ===
 * 'reaction'`. The callback receives the raw push `data` payload.
 *
 * The hook also auto-fires on `window` focus and `visibilitychange`
 * (returning to foreground on mobile) so a missed push is still
 * picked up the next time the user looks at the screen.
 *
 * @param {(data: Record<string, any>) => void} callback
 * @param {(data: Record<string, any>) => boolean} [filter]
 */
export default function usePushBus(callback, filter = null) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (e) => {
      const data = e?.detail || {};
      if (filter && !filter(data)) return;
      try { callback(data); } catch (_) { /* swallow consumer errors */ }
    };
    const onFocus = () => callback({ _trigger: 'focus' });
    const onVisibility = () => {
      if (!document.hidden) callback({ _trigger: 'visibility' });
    };
    window.addEventListener('slimmy:push', handler);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('slimmy:push', handler);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback]);
}
